<template>
    <div v-if="errors.length > 0" class="DocumentDataReviewErrors">
        <div class="DocumentDataReviewErrors__verticalBar" />
        <div class="DocumentDataReviewErrors__content">
            <EidIcon
                class="DocumentDataReviewErrors__icon"
                :icon-src="iconWarning"
                alt="warning icon"
            />
            <div class="DocumentDataReviewErrors__message">
                <span class="font-medium">{{ currentError.requirementTypeLabel }}</span>
                <span> - {{ currentError.fieldName }} has an error. Please review this field before accepting.</span>
            </div>
            <div class="DocumentDataReviewErrors__navigation">
                <button
                    class="DocumentDataReviewErrors__navButton"
                    :disabled="currentIndex === 0"
                    @click="navigateToPrevious"
                >
                    <EidIcon :icon-src="iconChevronLeft" alt="previous" />
                </button>
                <span class="DocumentDataReviewErrors__navCounter">
                    {{ currentIndex + 1 }}/{{ errors.length }} Errors
                </span>
                <button
                    class="DocumentDataReviewErrors__navButton"
                    :disabled="currentIndex === errors.length - 1"
                    @click="navigateToNext"
                >
                    <EidIcon :icon-src="iconChevronRight" alt="next" />
                </button>
            </div>
        </div>
    </div>
</template>

<style lang="scss">
.DocumentDataReviewErrors {
    display: flex;
    background: var(--eidDangerRed-10);
    border-radius: 4px;
    overflow: hidden;
    min-height: 48px;
    align-items: stretch;

    &__verticalBar {
        width: 4px;
        background: var(--eidDangerRed);
        flex-shrink: 0;
    }

    &__content {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 12px 16px;
        flex: 1;
    }

    &__icon {
        flex-shrink: 0;
        width: 20px;
        height: 20px;

        svg {
            color: var(--eidDangerRed);
        }
    }

    &__message {
        flex: 1;
        color: var(--eidDangerRed);
        font-size: 14px;
        line-height: 20px;
    }

    &__navigation {
        display: flex;
        align-items: center;
        gap: 8px;
        flex-shrink: 0;
    }

    &__navButton {
        background: none;
        border: none;
        color: var(--eidDangerRed);
        cursor: pointer;
        padding: 4px;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: opacity 0.2s;

        &:disabled {
            opacity: 0.4;
            cursor: not-allowed;
        }

        &:not(:disabled):hover {
            opacity: 0.7;
        }

        svg {
            width: 16px;
            height: 16px;
        }
    }

    &__navCounter {
        color: var(--eidDangerRed);
        font-size: 14px;
        line-height: 20px;
        min-width: 40px;
        text-align: center;
    }
}
</style>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import { faAngleLeft, faAngleRight } from '@fortawesome/pro-light-svg-icons';
    import { faExclamationTriangle } from '@fortawesome/pro-regular-svg-icons';

    import EidIcon from '@evidentid/dashboard-commons/components/EidIcon/EidIcon.vue';
    import { TprmRequirementType } from '@evidentid/tprm-portal-lib/models/entity-details';

    import { useDocumentDataReviewStore } from '@/modules/document-data-review/store';
    import { buildFormFieldId } from '@/modules/document-data-review/utils/requirement-form-field-id-utils/requirementFormFieldIdUtils';
    import { getDdrIdFromFormId, getRequirementTypeFromFormId } from '@/modules/document-data-review/utils/requirement-form-id-utils/requirementFormIdUtils';
    import { FieldPath } from '@/modules/dynamic-form/models/FieldPath.model';
    import { StoreFormInstance, useDynamicFormStore } from '@/modules/dynamic-form/store';
    import { highlightElement } from '@/utils/highlight-element/';
    import { useDashboardStore } from '@/modules/dashboard/vuex/dashboard';

    import { scrollToErrorField } from '../scrollToErrorField';
    interface DdrError {
        requirementTypeLabel: string;
        fieldName: string;
        requirementType: string;
        fieldPath: string;
        formId: string;
    }

    export default defineComponent({
        name: 'DocumentDataReviewErrors',
        components: {
            EidIcon,
        },
        props: {
            documentDataReviewId: {
                type: String as PropType<string>,
                required: true,
            },
            scrollOnInit: {
                type: Boolean as PropType<boolean>,
                default: true,
            },
        },
        data() {
            return {
                iconWarning: faExclamationTriangle,
                iconChevronLeft: faAngleLeft,
                iconChevronRight: faAngleRight,
                currentIndex: 0,
            };
        },
        computed: {
            errors(): DdrError[] {
                const self = this as any;
                const forms = useDynamicFormStore().forms || {};
                const errorsList: DdrError[] = [];

                // Find all forms matching pattern `${documentDataReviewId}::${requirementType}`
                Object.keys(forms).forEach((formKey) => {
                    const extractedDdrId = getDdrIdFromFormId(formKey);
                    if (!extractedDdrId || extractedDdrId !== self.documentDataReviewId) {
                        return;
                    }
                    const requirementType = getRequirementTypeFromFormId(formKey);
                    if (!requirementType) {
                        return;
                    }

                    // Get form instance
                    const form: StoreFormInstance = forms[formKey];
                    if (!form || !form.formErrors) {
                        return;
                    }

                    // Get requirementTypeLabel
                    const requirementTypeLabel = self.getRequirementTitle(requirementType);

                    Object.entries(form.fieldsMeta).forEach(([ fieldPathKey, fieldMeta ]) => {
                        const fieldError = form.formErrors[fieldPathKey as FieldPath];
                        if (!fieldError) {
                            return;
                        }
                        const fieldName = fieldMeta?.label || fieldPathKey;
                        errorsList.push({
                            requirementTypeLabel,
                            fieldName,
                            requirementType,
                            fieldPath: fieldPathKey,
                            formId: formKey,
                        });
                    });
                });

                return errorsList;
            },
            currentError(): DdrError {
                const self = this as any;
                return self.errors[self.currentIndex] || self.errors[0];
            },
        },
        watch: {
            errors: {
                handler(newErrors: DdrError[]) {
                    const self = this as any;
                    // Reset index if current index is out of bounds
                    if (self.currentIndex >= newErrors.length) {
                        self.currentIndex = Math.max(0, newErrors.length - 1);
                    }
                },
                immediate: true,
            },
        },
        mounted() {
            const self = this as any;
            if (self.scrollOnInit && self.errors.length > 0) {
                self.scrollToCurrentError();
            }
        },
        methods: {
            getRequirementTitle(requirement: TprmRequirementType): string {
                const self = this as any;
                return useDashboardStore().tprmRequirementsDefinitions[requirement]?.defaultLabel
                    || requirement;
            },
            navigateToPrevious(): void {
                const self = this as any;
                if (self.currentIndex > 0) {
                    self.currentIndex--;
                    self.scrollToCurrentError();
                }
            },
            navigateToNext(): void {
                const self = this as any;
                if (self.currentIndex < self.errors.length - 1) {
                    self.currentIndex++;
                    self.scrollToCurrentError();
                }
            },
            async scrollToCurrentError(): Promise<void> {
                const self = this as any;
                const error = self.currentError;
                if (!error) {
                    return;
                }

                const formId = error.formId;
                const fieldPath = error.fieldPath;
                const formFieldId = buildFormFieldId(formId, fieldPath);
                const foundField = document.getElementById(formFieldId);
                if (!foundField) {
                    return;
                }

                // Get form instance from store
                const ddrForm = useDocumentDataReviewStore()
                    .requirementTypeComponentRefs[self.documentDataReviewId] || {};
                if (
                    !ddrForm.ensureRequirementExpanded
                ) {
                    return;
                }

                // Check if expansion is needed and only wait if it was triggered
                const triggeredExpansion = ddrForm.ensureRequirementExpanded(error.requirementType);
                if (triggeredExpansion) {
                    // Wait for expansion animation to complete
                    await new Promise((resolve) => setTimeout(resolve, 200));
                }

                // Scroll to the field with offset for sticky header
                const scrolled = await scrollToErrorField(foundField);

                if (scrolled) {
                    highlightElement(foundField, {
                        color: 'var(--eidDangerRed-10)',
                        duration: 1000,
                        inset: '-12px',
                        radius: '8px',
                    });
                }
            },
        },
    });
</script>
